import { useId } from 'react';
import type { ReactNode } from 'react';

interface Props {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  variant?: 'light' | 'dark';
}

export default function FormSection({ title, isOpen, onToggle, children, variant = 'light' }: Props) {
  const contentId = useId();
  const isDark = variant === 'dark';

  return (
    <div
      className={`rounded-lg overflow-hidden ${
        isDark ? 'bg-brand-primary-deep' : 'bg-surface-app-elevated border border-border-strong'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className={`w-full px-4 py-3 flex items-center justify-between text-left
                   transition-colors focus:outline-none focus:ring-2
                   focus:ring-brand-primary/50 focus:ring-inset ${
                     isDark ? 'hover:bg-white/10' : 'hover:bg-surface-app-elevated'
                   }`}
      >
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-ink-primary'}`}>{title}</h2>
        <svg
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${
            isDark ? 'text-white/70' : 'text-ink-secondary'
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        id={contentId}
        role="region"
        aria-labelledby={contentId}
        hidden={!isOpen}
        className={isOpen ? 'px-4 pb-4 space-y-4' : ''}
      >
        {isOpen && children}
      </div>
    </div>
  );
}
